import { useFormikContext } from 'formik';
import { type MutableRefObject, useCallback, useContext, useEffect, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { QuoteFormikType } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import { ExceedingDiscounts, pendingDiscounts } from '@core/utils/discounts';
import { hasStringValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import {
  applyProjectPricesAsOriginal,
  applyTierPricingToProductsByPlants,
} from '@core/utils/quoteHelpers';
import { isValidUUID } from '@core/utils/uuid';
import { ServerErrorMessage, useSlabQueryFetcher } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { useQuoteDrawerContext } from '../QuoteDrawer';

export type ProductRowsOverride = Pick<
  QuoteFormikType,
  'mainProductsByPlants' | 'additionalProductsByPlants'
>;

export const useBuyerPricingReconciler = (
  curProductsRef: MutableRefObject<Record<string, Product>>,
): ((newCompanyID: string | null, productsOverride?: ProductRowsOverride) => Promise<void>) => {
  const ctx = useContext(SlabContext);
  const { marginConfiguration, defaultMargins } = useTenantMargins();
  const formikBag = useFormikContext<QuoteFormikType>();
  const latestValuesRef = useRef(formikBag.values);
  latestValuesRef.current = formikBag.values;

  const hasTierPricing =
    ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    ctx.userInfo.tenant.tierPricing;
  const hasDiscounts = ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagDiscounts]);

  const fetchTierPricing = useSlabQueryFetcher<'GET tier pricing by company ID'>();
  const fetchProjectProducts = useSlabQueryFetcher<'GET project products by project ID'>();
  const toast = useToast();
  const { setIsReconcilingBuyerPricing } = useQuoteDrawerContext();
  const latestCallRef = useRef(0);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
      setIsReconcilingBuyerPricing?.(false);
    };
  }, [setIsReconcilingBuyerPricing]);

  const reconcileRef = useRef<
    (newCompanyID: string | null, productsOverride?: ProductRowsOverride) => Promise<void>
  >(async (): Promise<void> => {});
  reconcileRef.current = async (
    newCompanyID: string | null,
    productsOverride?: ProductRowsOverride,
  ): Promise<void> => {
    latestCallRef.current += 1;
    const callToken = latestCallRef.current;
    const rowsFor = (section: keyof ProductRowsOverride): QuoteFormikType[typeof section] =>
      productsOverride?.[section] ?? latestValuesRef.current[section];
    const markDiscountsPending = (): void => {
      (['mainProductsByPlants', 'additionalProductsByPlants'] as const).forEach((section) => {
        SetFormikValue(
          formikBag,
          section,
          rowsFor(section).map((plantEntry) => ({
            ...plantEntry,
            products: plantEntry.products.map((product) =>
              (product.discounts ?? []).length > 0
                ? { ...product, discounts: pendingDiscounts(product.discounts ?? []) }
                : product,
            ),
          })),
        );
      });
    };
    if (!hasTierPricing) {
      if (!hasDiscounts) {
        return;
      }
      markDiscountsPending();
      return;
    }
    setIsReconcilingBuyerPricing?.(true);
    try {
      const projectId = latestValuesRef.current.project.id;
      const [fixed, projectProducts] = await Promise.all([
        hasStringValue(newCompanyID)
          ? fetchTierPricing('GET tier pricing by company ID', {
              queryParams: { companyIds: [newCompanyID] },
            })
          : Promise.resolve(undefined),
        isValidUUID(latestValuesRef.current.id) && hasStringValue(projectId)
          ? fetchProjectProducts('GET project products by project ID', {
              pathParams: { id: projectId },
            })
          : Promise.resolve(undefined),
      ]);
      if (!isMountedRef.current || latestCallRef.current !== callToken) {
        return;
      }
      const displayOnlyContext = { curProductsRef, marginConfiguration, defaultMargins };
      const sourceBySection = {
        mainProductsByPlants: applyProjectPricesAsOriginal(
          rowsFor('mainProductsByPlants'),
          projectProducts ?? [],
        ),
        additionalProductsByPlants: rowsFor('additionalProductsByPlants'),
      };
      (['mainProductsByPlants', 'additionalProductsByPlants'] as const).forEach((section) => {
        SetFormikValue(
          formikBag,
          section,
          applyTierPricingToProductsByPlants(
            sourceBySection[section],
            fixed,
            displayOnlyContext,
            ExceedingDiscounts.Unapply,
            true,
          ),
        );
      });
    } catch (error) {
      if (isMountedRef.current && latestCallRef.current === callToken) {
        if (hasDiscounts) {
          markDiscountsPending();
        }
        toast.error({
          title: 'Could not load buyer pricing',
          message: ServerErrorMessage(
            error,
            'Current prices were kept and discounts were rechecked.',
          ),
        });
      }
    } finally {
      if (isMountedRef.current && latestCallRef.current === callToken) {
        setIsReconcilingBuyerPricing?.(false);
      }
    }
  };

  return useCallback(
    (newCompanyID: string | null, productsOverride?: ProductRowsOverride): Promise<void> =>
      reconcileRef.current(newCompanyID, productsOverride),
    [],
  );
};
